import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, MaskedLines } from "@/components/Reveal";

export function Eyebrow({ children, dark = false }) {
  return (
    <p className={`font-mono text-xs uppercase tracking-[0.22em] font-medium ${dark ? "text-volt" : "text-emerald-600"}`}>
      {children}
    </p>
  );
}

export function SectionHead({ eyebrow, title, lead, dark = false, className = "" }) {
  return (
    <Reveal className={`max-w-3xl ${className}`}>
      {eyebrow && <Eyebrow dark={dark}>{eyebrow}</Eyebrow>}
      <h2 className={`mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-snug text-balance ${dark ? "text-white" : "text-slate-900"}`}>
        {title}
      </h2>
      {lead && (
        <p className={`mt-5 text-base sm:text-lg leading-relaxed ${dark ? "text-slate-400" : "text-slate-600"}`}>
          {lead}
        </p>
      )}
    </Reveal>
  );
}

export function PageHero({ eyebrow, lines, lead, dark = true, children, testid }) {
  return (
    <section
      data-testid={testid}
      className={`relative overflow-hidden pt-32 sm:pt-40 pb-16 sm:pb-24 ${
        dark ? "bg-obsidian grid-lines-dark noise-overlay" : "bg-mist grid-lines-light"
      }`}
    >
      {dark && <div className="absolute inset-0 hero-glow pointer-events-none" aria-hidden="true" />}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal y={16}>
          <Eyebrow dark={dark}>{eyebrow}</Eyebrow>
        </Reveal>
        <MaskedLines
          lines={lines}
          className={`mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.08] text-balance max-w-4xl ${dark ? "text-white" : "text-slate-900"}`}
        />
        {lead && (
          <Reveal delay={0.35} className="max-w-2xl">
            <p className={`mt-6 text-base sm:text-lg leading-relaxed ${dark ? "text-slate-400" : "text-slate-600"}`}>{lead}</p>
          </Reveal>
        )}
        {children}
      </div>
    </section>
  );
}

export function CTABand({ title, text, primary, secondary, testid = "cta-band" }) {
  return (
    <section data-testid={testid} className="relative overflow-hidden bg-obsidian grid-lines-dark noise-overlay">
      <div className="absolute inset-0 hero-glow pointer-events-none" aria-hidden="true" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <Reveal className="max-w-3xl">
          <Eyebrow dark>Work With BNM</Eyebrow>
          <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-snug text-white text-balance">
            {title}
          </h2>
          {text && <p className="mt-5 text-base sm:text-lg leading-relaxed text-slate-400">{text}</p>}
          <div className="mt-8 flex flex-wrap gap-4">
            {primary && (
              <Link
                to={primary.to}
                data-testid={primary.testid || "cta-primary"}
                className="inline-flex items-center gap-2 rounded-md bg-volt px-7 py-3.5 font-semibold text-obsidian transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-volt-soft hover:text-obsidian active:translate-y-0"
              >
                {primary.label}
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            )}
            {secondary && (
              <Link
                to={secondary.to}
                data-testid={secondary.testid || "cta-secondary"}
                className="inline-flex items-center gap-2 rounded-md border border-white/20 px-7 py-3.5 font-semibold text-white transition-[transform,background-color,border-color] duration-200 hover:-translate-y-0.5 hover:border-volt/60 hover:text-volt active:translate-y-0"
              >
                {secondary.label}
              </Link>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
